import { tx, txt, useContent } from "@/content/ContentContext";
import { Check, Sparkles } from "lucide-react";
import { formatPlanPrice } from "@/lib/plans";
import { useContact } from "@/context/ContactContext";

// mode "landing": demo CTA; mode "upgrade": request-upgrade action.
const limit = (n, one, many) => (n == null ? `Unlimited ${many}` : `${n} ${n === 1 ? one : many}`);

export function PlanCards({ plans, currency, mode, onAction, busyCode, requestedCode, period = "monthly" }) {
  useContent();
  const contact = useContact();
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="plan-cards">
      {plans.map((p) => {
        const hot = p.popular;
        return (
          <div
            key={p.code}
            data-testid={`plan-card-${p.code}`}
            className={`relative flex flex-col rounded-2xl border p-6 transition-[transform,box-shadow] hover:-translate-y-1 ${
              hot ? "border-slate-950 bg-slate-950 text-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.8)]" : "border-slate-200 bg-white text-slate-900"
            }`}
          >
            {hot && (
              <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-950">
                <Sparkles className="h-3 w-3" /> {txt("landing.most_popular")}
              </span>
            )}
            <div className="font-display text-xl font-bold">{p.name}</div>
            <div className={`mt-1 text-sm ${hot ? "text-slate-400" : "text-slate-500"}`}>{p.tagline}</div>
            <div className="mt-6 flex items-baseline gap-1.5" data-testid={`plan-price-${p.code}`}>
              {p.custom ? (
                <span className="font-display text-4xl font-extrabold tracking-tight">{txt("landing.custom")}</span>
              ) : (
                <>
                  <span className={`font-mono text-sm font-semibold ${hot ? "text-amber-400" : "text-slate-500"}`}>{currency}</span>
                  <span className="money text-4xl font-extrabold" data-testid={`plan-amount-${p.code}`}>{formatPlanPrice(period === "yearly" ? p.yearly_prices?.[currency] : p.prices[currency], currency)}</span>
                  <span className={`text-sm ${hot ? "text-slate-400" : "text-slate-500"}`}>{period === "yearly" ? "/year" : "/month"}</span>
                </>
              )}
            </div>
            {!p.custom && p.special_prices?.[currency] && (
              <div className="mt-2 rounded-lg bg-emerald-500/15 px-2.5 py-1.5 text-sm font-bold text-emerald-600" data-testid={`plan-special-price-${p.code}`}>
                Your special price: {currency} {formatPlanPrice(p.special_prices[currency], currency)}/month
              </div>
            )}
            <div className={`mt-3 font-mono text-[11px] uppercase tracking-wider ${hot ? "text-slate-400" : "text-slate-500"}`} data-testid={`plan-limits-${p.code}`}>
              {limit(p.limits?.users, "user", "users")} · {limit(p.limits?.branches, "branch", "branches")}
            </div>
            <ul className="mt-6 flex-1 space-y-2.5 text-sm">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className={`mt-0.5 h-4 w-4 shrink-0 ${hot ? "text-amber-400" : "text-emerald-600"}`} /> {f}
                </li>
              ))}
              {p.online_store && (
                <li className="flex items-start gap-2" data-testid={`plan-online-store-${p.code}`}>
                  <Check className={`mt-0.5 h-4 w-4 shrink-0 ${hot ? "text-amber-400" : "text-emerald-600"}`} /> {txt("landing.online_store")}
                  <span className="rounded bg-amber-400 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-slate-950">{txt("landing.premium")}</span>
                </li>
              )}
              {p.reports && (
                <li className="flex items-start gap-2" data-testid={`plan-reports-${p.code}`}>
                  <Check className={`mt-0.5 h-4 w-4 shrink-0 ${hot ? "text-amber-400" : "text-emerald-600"}`} /> {txt("landing.sales_reports")}
                  <span className="rounded bg-amber-400 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-slate-950">{txt("landing.premium")}</span>
                </li>
              )}
            </ul>
            <div className={`mt-6 ${mode === "view" ? "hidden" : ""}`}>
              {mode === "landing" && p.custom ? (
                <button onClick={() => contact("sales")} data-testid="plan-contact-sales" className="flex h-12 w-full items-center justify-center rounded-xl border-2 border-slate-900 font-semibold hover:bg-slate-900 hover:text-white">
                  {txt("landing.contact_sales")}
                </button>
              ) : mode === "landing" ? (
                <>
                  <button onClick={() => onAction(p)} data-testid={`plan-cta-${p.code}`} className={`flex h-12 w-full items-center justify-center rounded-xl font-semibold transition-colors ${hot ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                    {txt("landing.start_15_day_free_demo")}
                  </button>
                  <div className={`mt-2 text-center text-xs ${hot ? "text-slate-400" : "text-slate-500"}`}>{txt("landing.no_credit_card_required")}</div>
                </>
              ) : requestedCode === p.code ? (
                <button disabled data-testid={`plan-requested-${p.code}`} className="flex h-12 w-full cursor-default items-center justify-center gap-2 rounded-xl border-2 border-emerald-500 bg-emerald-50 font-semibold text-emerald-800">
                  <Check className="h-4 w-4" /> {txt("landing.request_received")}
                </button>
              ) : (
                <button
                  onClick={() => (p.custom ? contact("sales") : onAction(p))}
                  disabled={busyCode === p.code}
                  data-testid={`plan-request-${p.code}`}
                  className={`flex h-12 w-full items-center justify-center rounded-xl font-semibold transition-colors disabled:opacity-60 ${hot ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                >
                  {p.custom ? "Contact sales" : tx("landing.request_upgrade")}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
